<template>
  <div>
    <div
      v-for="(itemsFromGroup, type, groupedIndex) in groupedItems"
      :key="type"
      :style="{ '--type-color': types[type].color }"
      class="grammar-flags-base-items"
    >
      <eg-colored-dot-box
        :color="types[type].color"
        :background-color="types[type].background"
      />
      <div class="grammar-content d-flex flex-column">
        <div class="d-flex align-items-center">
          <h5 class="mb-0 heading-xs">{{ types[type].title }}</h5>
        </div>

        <slot name="additionalContentBeforeList" :type="type" />

        <component
          :is="collapsable ? 'eg-additional-collapse' : 'div'"
          v-if="itemsFromGroup.length"
          label="Show sentences"
        >
          <base-item
            v-for="(item, index) in itemsFromGroup"
            ref="items"
            :key="item.id"
            :class="['grammar-item', type]"
            :style="{ '--type-background': types[type].background }"
            :active-highlight-index="activeHighlightIndex"
            :index="calculateIndex(groupedIndex, index)"
            :item="item"
            next-steps-key="suggestions"
            :show-index="false"
            :next-step-title="nextStepTitle"
            :format-title="() => ''"
            v-on="$listeners"
          />
        </component>
      </div>
    </div>
  </div>
</template>

<script>
import BaseItem from "@/components/essay/essay/v3/base_item.vue";
import EgAdditionalCollapse from "@/components/global/eg_additional_collapse.vue";
import EgColoredDotBox from "@/components/global/eg/eg-colored-dot-box.vue";

export default {
  name: "GrammarFlagsBaseItems",

  components: {
    BaseItem,
    EgAdditionalCollapse,
    EgColoredDotBox,
  },

  props: {
    groupedItems: {
      type: Object,
      required: true,
    },

    types: {
      type: Object,
      required: true,
    },

    activeHighlightIndex: {
      type: Number,
      required: true,
    },

    isNoRanges: {
      type: Boolean,
      default: false,
    },

    collapsable: {
      type: Boolean,
      default: false,
    },

    nextStepTitle: {
      type: String,
      default: "Pro tip",
    },
  },

  watch: {
    groupedItems: {
      immediate: true,
      handler() {
        if (this.isNoRanges) return;

        this.$nextTick(() => {
          const highlights = Object.keys(this.groupedItems).reduce(
            (acum, type, groupedIndex) => {
              const itemsFromGroup = this.groupedItems[type];

              for (let index = 0; index < itemsFromGroup.length; index++) {
                const item = itemsFromGroup[index];
                const realIndex = this.calculateIndex(groupedIndex, index);

                if (!item.ranges) {
                  return acum;
                }

                for (
                  let rangeIndex = 0;
                  rangeIndex < item.ranges.length;
                  rangeIndex++
                ) {
                  acum.push({
                    range: item.ranges[rangeIndex],
                    showHighlightIndex: false,
                    highlightIndex: realIndex + 1,
                    refComponent: this.$refs.items?.[realIndex],
                    color: this.types[type].color,
                    backgroundColor: this.types[type].background,
                  });
                }
              }

              return acum;
            },
            []
          );
          this.$emit("updateHighlights", highlights);
        });
      },
    },
  },

  methods: {
    calculateIndex(groupedIndex, index) {
      return (
        Object.keys(this.groupedItems)
          .slice(0, groupedIndex)
          .reduce((sum, key) => sum + this.groupedItems[key].length, 0) + index
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.grammar-flags-base-items {
  display: flex;
  padding: 16px 24px 0 24px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

h5 {
  font-family: "Inter", sans-serif;
  line-height: 24px; // eg-colored-dot-box size
  color: var(--color-text-neutral-on-white);
}

.grammar-item {
  border-bottom: 1px solid #dfdfdf;
  margin-right: -24px;
  margin-left: -64px;
  padding-left: 64px;

  &::v-deep {
    & > div {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;

      .content-container {
        width: 100%;
        overflow: auto;
        word-break: normal;
        overflow-wrap: anywhere;
        position: relative;
      }

      .actions {
        margin-top: 0 !important;
      }
    }
  }
}

.grammar-content {
  width: calc(100% - 40px) !important;
  gap: 0.5rem;
}

.grammar-item.spelling {
  &:nth-child(1) {
    margin-top: 16px;
  }
}
</style>
